// Blocking Tasks section — dashboard parity v3.4.
//
// Sits in the Employee Analytics group, after Team Capacity and
// before Most Overdue. Mirrors v1's "Tasks blocking others"
// chart: per-staffer count of their open Commitments that are the
// upstream of a finish-to-start dependency held by someone else's
// open Commitment. Click-through routes to the Tracker filtered
// to ``assigned_to=<user>&is_blocking=true``.

import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useBlockingTasksQuery,
  useCommonFilters,
} from "@/features/dashboard";


export const BLOCKING_TASKS_SECTION = "blocking-tasks" as const;


// Match the Workload chart's pager cadence so the page-size feel is
// consistent across staff sections.
const VISIBLE_LIMIT = 50;
const PAGE_SIZE = 6;


export function BlockingTasksSection() {
  const filters = useCommonFilters();
  const query = useBlockingTasksQuery(filters);
  const rows = (query.data?.rows ?? []).slice(0, VISIBLE_LIMIT);
  const max = rows.reduce((m, r) => Math.max(m, r.blocking_count), 1);

  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const visible = rows.slice(start, start + PAGE_SIZE);
  const showPager = rows.length > PAGE_SIZE;

  return (
    <DashboardCard
      section={BLOCKING_TASKS_SECTION}
      title="Blocking work"
      subtitle="Open Commitments your staff own that are gating someone else's work."
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={4} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load blocking work"
          retry={() => query.refetch()}
        />
      ) : rows.length === 0 ? (
        <DashboardEmptyState
          title="Nothing is blocking other work right now"
          description="When a staffer's open Commitment becomes the upstream of someone else's dependency, they'll appear here."
        />
      ) : (
        <ul role="list" className="flex flex-col gap-2">
          {visible.map((row) => (
            <li
              key={row.user_id}
              data-testid={`blocking-tasks-row-${row.user_id}`}
              className="grid grid-cols-[160px_1fr_50px] items-center gap-3"
            >
              <Link
                to={row.drill_through_url}
                data-testid={`blocking-tasks-link-${row.user_id}`}
                className="truncate text-xs font-medium text-text-primary hover:underline"
                title={row.display_name}
              >
                {row.display_name}
              </Link>
              <div className="h-3 rounded-full bg-recessed/40">
                <div
                  className="h-full rounded-full bg-ember"
                  style={{
                    width: `${Math.max(2, (row.blocking_count / max) * 100)}%`,
                  }}
                />
              </div>
              <span
                data-testid={`blocking-tasks-count-${row.user_id}`}
                className="text-right text-[11px] font-semibold text-ember tabular-nums"
              >
                {row.blocking_count}
              </span>
            </li>
          ))}
        </ul>
      )}
      {showPager && (
        <footer
          data-testid="blocking-tasks-pager"
          className="flex items-center justify-between text-[11px] text-text-tertiary"
        >
          <span>
            {start + 1}–{start + visible.length} of {rows.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="blocking-tasks-pager-prev"
              aria-label="Previous staff"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <ChevronLeft size={14} aria-hidden="true" />
            </button>
            <span className="tabular-nums">
              {safePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              data-testid="blocking-tasks-pager-next"
              aria-label="Next staff"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </footer>
      )}
    </DashboardCard>
  );
}
